import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import c from 'classnames';
import { IconArrowLink } from '../../../../assets/icons';
import { DeviceConnectionState } from '../../../../data-access/gql-types/graphql';
import { ROUTES } from '../../../../routes/routes';
import { useDevicesAndChannels } from '../../hooks/devices-and-channels';
import { useConfiguration } from '../../modules/channel-configuration/hooks/use-configuration';
import { useChannelsStateStore } from '../../store/channels-state';
import { DeviceType } from '../../types';
import { checkDeviceStatus } from '../../utils/devices/status';
import DeviceIcon from '../device-icon';
import './index.scss';

type DeviceProps = {
  device: DeviceType;
};

export const Device: React.FC<DeviceProps> = ({ device }) => {
  const navigate = useNavigate();
  const { deviceChannels } = useDevicesAndChannels({ deviceId: device.id });
  const { t } = useTranslation('common');
  const { t: ti } = useTranslation('device-info');
  const channelState = useChannelsStateStore((s) => s.channelState);
  const { updateDevice, updateDeviceLoading, updateRecoveryDevice } = useConfiguration({ device });
  const isDeviceDisconnected = checkDeviceStatus(deviceChannels, channelState, DeviceConnectionState.Disconnected);
  const isDeviceInRecoveryState = checkDeviceStatus(deviceChannels, channelState, DeviceConnectionState.Recovery);

  if (!device) return null;

  return (
    <div className="driver">
      <div className="driver__link" onClick={() => navigate(`${ROUTES.DeviceSettings(device.id)}?from=list`)}>
        <DeviceIcon
          model={device.payload.model}
          disconnected={isDeviceDisconnected}
          recovery={isDeviceInRecoveryState}
        />
        <div className="driver__header">
          <div className="driver__header-title">
            <div className="driver__header-title-name">{device.payload.name || 'N/A'}</div>
            <div className="driver__header-title-type">{device.payload.model || t('types.unknown')}</div>
          </div>
        </div>
      </div>
      {device.payload.isUpdateAvailable ? (
        <div className="driver__update">
          {!updateDeviceLoading ? (
            <p className="driver__update-version">
              <span>{device.payload.currentFirmwareVersion}</span>
              <IconArrowLink size={10} className="icon-arrow-link--accent" />{' '}
              <span>{device.payload.newestFirmwareVersion}</span>
            </p>
          ) : null}

          {isDeviceInRecoveryState ? (
            <button
              className="button button--accent update"
              disabled={isDeviceDisconnected}
              onClick={updateRecoveryDevice}
              type="button"
            >
              <span className="update">{ti('updateSoftware.update')}</span>
            </button>
          ) : (
            <button
              className="button button--accent update"
              disabled={updateDeviceLoading || isDeviceDisconnected}
              onClick={updateDevice}
              type="button"
            >
              <span className={c({ update: !updateDeviceLoading })}>
                {!updateDeviceLoading ? ti('updateSoftware.update') : t('buttons.loading')}
              </span>
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};
